import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { useWave } from '@/hooks/useWave';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface WaveButtonProps {
  recipientId: string;
  recipientName?: string;
  size?: 'sm' | 'md';
  className?: string;
}

export function WaveButton({ recipientId, recipientName, size = 'md', className }: WaveButtonProps) {
  const { user } = useAuth();
  const { sendWave, isSending, canWave, hasWavedRecently, hasReachedDailyLimit } = useWave();
  const [justWaved, setJustWaved] = useState(false);

  // Reset justWaved after animation
  useEffect(() => {
    if (justWaved) {
      const timer = setTimeout(() => setJustWaved(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [justWaved]);

  // Don't show for own profile or when not logged in
  if (!user || user.id === recipientId) {
    return null;
  }

  const isOnCooldown = hasWavedRecently(recipientId);
  const canWaveNow = canWave(recipientId);

  const handleWave = () => {
    if (!canWaveNow || isSending) return;
    
    sendWave({ recipientId, recipientName }, {
      onSuccess: () => {
        setJustWaved(true);
      },
    });
  };

  const getTooltipText = () => {
    if (justWaved) return 'Waved!';
    if (hasReachedDailyLimit) return 'Daily wave limit reached';
    if (isOnCooldown) return 'You waved recently';
    return 'Wave';
  };

  const isSmall = size === 'sm';

  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            onClick={handleWave}
            disabled={!canWaveNow || isSending}
            variant={isOnCooldown || justWaved ? "secondary" : "outline"}
            size={isSmall ? "icon" : "sm"}
            className={cn(
              "relative overflow-hidden transition-all duration-200",
              isSmall ? "h-8 w-8" : "h-9 gap-1.5 px-3",
              isOnCooldown && "opacity-60",
              className
            )}
          >
            <AnimatePresence mode="wait">
              {isSending ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                >
                  <Loader2 className={cn("animate-spin", isSmall ? "w-4 h-4" : "w-4 h-4")} />
                </motion.div>
              ) : justWaved ? (
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex items-center gap-1"
                >
                  <Check className={cn("text-green-500", isSmall ? "w-4 h-4" : "w-4 h-4")} />
                  {!isSmall && <span className="text-green-500">Waved!</span>}
                </motion.div>
              ) : (
                <motion.div
                  key="wave"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex items-center gap-1"
                >
                  <span className={cn(
                    "inline-block",
                    !isOnCooldown && "animate-wave"
                  )}>
                    👋
                  </span>
                  {!isSmall && !isOnCooldown && <span>Wave</span>}
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="text-xs">
          {getTooltipText()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
